/**
 * Same-origin relative path only. Rejects absolute URLs, protocol-relative
 * paths ({@link https://example.com//evil.example}), and backslash variants
 * ({@link https://example.com/\evil.example}) that browsers resolve as an
 * authority component, which would otherwise open-redirect off-site.
 *
 * Resolved against the real page origin (not a synthetic placeholder) so a
 * network-path reference can only ever match this site's own origin, never
 * an attacker-chosen one.
 */
function isSafeRedirectPath(path: string): boolean {
  if (!path.startsWith("/")) {
    return false;
  }

  const { origin } = window.location;

  try {
    return new URL(path, origin).origin === origin;
  } catch {
    return false;
  }
}

const RETURN_TO_KEY = "up.signin.returnTo";

/**
 * Persists a same-origin path in `sessionStorage` rather than a `redirect`
 * query string, since the URL itself is what the PCI scanner flags. Storage
 * (vs. e.g. component state) is needed because some call sites are plain
 * `<a href>` links — not client-side navigations — so the value must survive
 * a full page load. No-ops on an unsafe path or when `sessionStorage` is
 * unavailable/throws.
 */
export function rememberReturnTo(path: string): void {
  if (!isSafeRedirectPath(path)) {
    return;
  }

  try {
    sessionStorage.setItem(RETURN_TO_KEY, path);
  } catch {
    // sessionStorage unavailable (e.g. private browsing); nothing to do.
  }
}

/**
 * Reads and removes the stored return-to path (consume-once semantics),
 * re-validating it before returning. Returns `null` if nothing was stored,
 * the stored value is unsafe, or `sessionStorage` is unavailable/throws.
 */
export function consumeReturnTo(): string | null {
  try {
    const value = sessionStorage.getItem(RETURN_TO_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);

    return value && isSafeRedirectPath(value) ? value : null;
  } catch {
    return null;
  }
}

export function followRedirectParam(
  router: { push: (url: string) => void } | null,
) {
  const urlParams = new URLSearchParams(window.location.search);
  const redirect = urlParams.get("redirect");
  const destination =
    consumeReturnTo() ??
    (redirect && isSafeRedirectPath(redirect) ? redirect : "/");

  if (router) {
    router.push(destination);
  } else {
    window.location.href = destination;
  }
}
